import { Box, Divider, Stack, Typography } from "@mui/material";
import {
  CheckBox,
  DropdownMenu,
  EditableChip,
  Horizontal3DotsIcon,
  InfoIcon,
  KbFileDualIcon,
  KbFolderDualIcon,
} from "components";
import { useRef, useState } from "react";
import theme from "utilz/theme/theme";
import {
  useGetKbFolderContents,
  useGlobalStore,
  usePermissionStore,
  useUpdateKbFolder,
} from "controller";
import { claculateTimeElapsed, isoToDateAndTime } from "utilz/helpers";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { selectedKbItemIdsArr, toggleKbSelectionFn } from "../store";
import { fileActionOptions } from "../constants";
import { KbThreeDots } from "./KbThreeDots";
import { PERMISSIONS } from "shared";
import toast from "react-hot-toast";
import { KbRecordVisibilityChip } from "./KbRecordVisibilityChip";

export const KbListViewFileCard = ({ fileDetails }: { fileDetails: any }) => {
  return <KbListViewCard type="file" details={fileDetails} />;
};

export const KbListViewFolderCard = ({
  folderDetails,
}: {
  folderDetails: any;
}) => {
  return <KbListViewCard type="folder" details={folderDetails} />;
};

const KbListViewCard = ({
  details,
  type,
}: {
  details: any;
  type: "file" | "folder";
}) => {
  const navigate = useNavigate();
  const timezone = useGlobalStore((state) => state.timezone);
  const chipRef = useRef();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { mutateAsync: updateKbFolder } = useUpdateKbFolder();
  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const selectedKbItemIds = useAtomValue(selectedKbItemIdsArr);
  const isSelected = selectedKbItemIds?.includes(details?.mapping_id) || false;
  const toggleSelection = useSetAtom(toggleKbSelectionFn);

  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    type === "folder"
      ? navigate(`/app/kb/folder/${details?.mapping_id}`)
      : navigate(`/app/kb/document/${details?.article_id}`);
  };

  const handleNameChange = async (name: string) => {
    try {
      await updateKbFolder({
        folderId: details?.mapping_id,
        folderData: {
          name,
        },
      });
      toast.success("Folder renamed successfully");
    } catch (error) {
      toast.error("Failed to rename folder");
    }
  };

  const handleSingleClick = () => {
    if (clickTimeoutRef.current) {
      return;
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (details?.is_customer_folder || !details.parent_id) {
        toast.custom(
          <Stack
            sx={{
              p: "10px 16px",
              backgroundColor: theme.palette.lightBg.medium,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <InfoIcon sx={{ color: "#FFF" }} />
            <Typography variant="body-medium" color={"#FFF"}>
              Cannot select default folder
            </Typography>
          </Stack>,
          { position: "bottom-center", duration: 2000 },
        );
      } else {
        toggleSelection(details?.mapping_id);
      }
      clickTimeoutRef.current = null;
    }, 200);
  };

  return (
    <Stack
      onClick={handleSingleClick}
      sx={{
        flexDirection: "row",
        alignItems: "center",
        p: "8px",
        gap: "8px",
        bgcolor: isSelected
          ? theme.palette.secondary["purple-tint-3"]
          : "transparent",
        border: `1px solid ${
          isSelected ? theme.palette.primary.main : theme.palette.neutral["010"]
        }`,
        boxShadow: "none",
        borderRadius: "4px",
        cursor: "pointer",
        overflow: "hidden",
        ":hover": {
          boxShadow:
            "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      {/* <CheckBox
        sx={{
          visibility: isHovered || isSelected ? "visible" : "hidden",
        }}
        checked={isSelected}
        onChange={() => toggleSelection(details?.mapping_id)}
      /> */}
      {type === "folder" ? (
        <KbFolderDualIcon
          color="primary"
          style={{
            width: "24px",
            height: "24px",
          }}
        />
      ) : (
        <KbFileDualIcon
          color="primary"
          style={{
            width: "24px",
            height: "24px",
          }}
        />
      )}
      {type === "folder" ? (
        <Box
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <EditableChip
            ref={chipRef}
            disabled={
              isSelected || details?.is_customer_folder || !details.parent_id
                ? true
                : false
            }
            value={details?.name}
            onEdit={handleNameChange}
            inputSx={{
              ...theme.typography["body-small"],
              px: 0,
            }}
            sx={{
              maxWidth: "140px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...theme.typography["caption-2"],
            }}
            typographySx={{
              px: 0,
              ...theme.typography["caption-2"],
            }}
            onNavigate={handleDoubleClick}
          />
        </Box>
      ) : (
        <Typography
          onClick={(e) => navigate(`/app/kb/document/${details?.article_id}`)}
          variant="caption-2"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            ":hover": {
              cursor: "pointer",
              color: theme.palette.primary.main,
              textDecoration: "underline",
            },
          }}
        >
          {details?.name}
        </Typography>
      )}

      <Stack
        sx={{
          ml: "auto",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
        }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {type === "folder" && (
          <Typography variant="body-xsmall">
            {details?.children?.length} Items
          </Typography>
        )}
        <Typography
          variant="body-xsmall"
          noWrap
          color={theme.palette.lightBg.medium}
        >
          Created on{" "}
          {isoToDateAndTime({
            isodate: details?.created_at,
            timezone,
            format: "DD MMM YYYY",
          })}
        </Typography>
        {type === "file" && (
          <Typography
            variant="body-xsmall"
            noWrap
            color={theme.palette.lightBg.medium}
          >
            Last modified{" "}
            {claculateTimeElapsed(Date.parse(details?.updated_at))} ago
          </Typography>
        )}

        {type === "file" && (
          <KbRecordVisibilityChip visibility={details?.record_visibility} />
        )}
        {haveFullAccess && (
          <KbThreeDots
            fileDetails={details}
            isSelected={isSelected}
            isDefaultFolder={
              details?.is_customer_folder || !details.parent_id ? true : false
            }
            type={type}
            onActionClick={(type) => {
              if (type === "rename") {
                // @ts-ignore
                chipRef?.current?.focus();
              }
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};
